import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { adminService, Service, ServiceMetrics, MetricSeries, ServiceDetailResponse } from '../../services/admin.service';

// ─── constants ───────────────────────────────────────────────────────────────

const StatusColors: Record<string, { bg: string; text: string }> = {
  RUNNING:    { bg: 'bg-green-100 dark:bg-green-900/20',  text: 'text-green-800 dark:text-green-200' },
  CREATING:   { bg: 'bg-blue-100 dark:bg-blue-900/20',   text: 'text-blue-800 dark:text-blue-200' },
  STOPPED:    { bg: 'bg-gray-100 dark:bg-gray-700',      text: 'text-gray-800 dark:text-gray-300' },
  TERMINATED: { bg: 'bg-red-100 dark:bg-red-900/20',     text: 'text-red-800 dark:text-red-200' },
  ERROR:      { bg: 'bg-red-100 dark:bg-red-900/20',     text: 'text-red-800 dark:text-red-200' },
  FAILED:     { bg: 'bg-red-100 dark:bg-red-900/20',     text: 'text-red-800 dark:text-red-200' },
  DELETING:   { bg: 'bg-orange-100 dark:bg-orange-900/20', text: 'text-orange-800 dark:text-orange-200' },
  DELETED:    { bg: 'bg-gray-100 dark:bg-gray-700',      text: 'text-gray-600 dark:text-gray-400' },
};

const TypeLabels: Record<string, string> = {
  APP_DEPLOY_STATIC: 'Sitio Estático',
  APP_DEPLOY_SERVER: 'App Servidor',
  COMPUTE:           'VM / Compute',
  DATABASE:          'Base de Datos',
  CLICKHOUSE:        'ClickHouse',
  FRAPPE_EDUCATION:  'Frappe Education',
  IDURAR:            'IDURAR',
  N8N:               'n8n',
  GHOST:             'Ghost',
  STRAPI:            'Strapi',
  ODOO:              'Odoo',
  METABASE:          'Metabase',
  RABBITMQ:          'RabbitMQ',
  REDIS:             'Redis',
  ONYX:              'Onyx',
  DOCKER:            'Docker',
  MEDUSA:            'Medusa',
  TOOLJET:           'ToolJet',
};

const RANGE_OPTIONS = [
  { label: '1h',  hours: 1,   period: 60 },
  { label: '3h',  hours: 3,   period: 300 },
  { label: '12h', hours: 12,  period: 600 },
  { label: '24h', hours: 24,  period: 900 },
  { label: '7d',  hours: 168, period: 3600 },
];

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatTs(iso: string, rangeHours: number): string {
  const d = new Date(iso);
  if (rangeHours <= 24) {
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  return d.toLocaleDateString('es-MX', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function seriesChartData(series: MetricSeries | undefined, rangeHours: number) {
  if (!series?.timestamps.length) return [];
  return series.timestamps.map((ts, i) => ({
    time: formatTs(ts, rangeHours),
    value: series.values[i] ?? 0,
  }));
}

function networkChartData(
  inSeries: MetricSeries | undefined,
  outSeries: MetricSeries | undefined,
  rangeHours: number,
) {
  if (!inSeries?.timestamps.length) return [];
  return inSeries.timestamps.map((ts, i) => ({
    time: formatTs(ts, rangeHours),
    entrada: inSeries.values[i] ?? 0,
    salida: outSeries?.values[i] ?? 0,
  }));
}

function statBadge(val: number | null, unit: string): string {
  if (val === null) return '—';
  return `${val}${unit === '%' ? '%' : ` ${unit}`}`;
}

// ─── StatCard ────────────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
    <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
    <p className="text-base font-semibold text-gray-900 dark:text-white mt-1 truncate">{value || '—'}</p>
  </div>
);

// ─── MetricsPanel ─────────────────────────────────────────────────────────────

const MetricsPanel: React.FC<{ serviceId: string; serviceType: string }> = ({
  serviceId, serviceType,
}) => {
  const [rangeIdx, setRangeIdx] = useState(0);
  const [metrics, setMetrics] = useState<ServiceMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const range = RANGE_OPTIONS[rangeIdx];
  const isRds = serviceType === 'DATABASE';

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const resp = await adminService.getServiceMetrics(serviceId, range.hours, range.period);
    if (resp.success && resp.data) {
      setMetrics(resp.data);
    } else {
      setError(resp.error || 'No se pudieron cargar las métricas');
    }
    setIsLoading(false);
  }, [serviceId, range.hours, range.period]);

  useEffect(() => { load(); }, [load]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Cargando métricas de CloudWatch…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
        <strong>Error al cargar métricas:</strong> {error}
      </div>
    );
  }

  if (metrics && !metrics.has_metrics) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="text-gray-500 dark:text-gray-400">
          {metrics.message || 'No hay métricas de infraestructura para este tipo de servicio.'}
        </p>
      </div>
    );
  }

  const m = metrics?.metrics ?? {};
  const cpuData = seriesChartData(m.cpu, range.hours);
  const netData = networkChartData(m.network_in, m.network_out, range.hours);
  const diskData = networkChartData(m.disk_read, m.disk_write, range.hours);

  return (
    <div className="space-y-6">
      {/* Range selector */}
      <div className="flex items-center gap-2">
        {RANGE_OPTIONS.map((opt, i) => (
          <button
            key={opt.label}
            onClick={() => setRangeIdx(i)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              i === rangeIdx
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto px-3 py-1.5 rounded-md text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualizar
        </button>
      </div>

      {/* No datapoints notice */}
      {metrics && cpuData.length === 0 && netData.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm text-yellow-700 dark:text-yellow-300">
          Sin puntos de datos en este rango. El servicio puede estar detenido o usar monitoreo básico (datos cada 5 min).
        </div>
      )}

      {/* CPU Chart */}
      {!isRds && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">CPU</h3>
            <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Actual: <strong className="text-gray-900 dark:text-white">{statBadge(m.cpu?.current ?? null, '%')}</strong></span>
              <span>Máx: <strong className="text-gray-900 dark:text-white">{statBadge(m.cpu?.max ?? null, '%')}</strong></span>
            </div>
          </div>
          {cpuData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={cpuData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                <Tooltip formatter={(v: any) => [`${v}%`, 'CPU']} />
                <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="url(#cpuGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-44 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
              Sin datos
            </div>
          )}
        </div>
      )}

      {/* Network In/Out Chart (EC2) */}
      {!isRds && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Red</h3>
            <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-blue-500" /> Entrada</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-purple-500" /> Salida</span>
            </div>
          </div>
          {netData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={netData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} unit=" KB" />
                <Tooltip formatter={(v: any, name: any) => [`${v} KB`, name === 'entrada' ? 'Entrada' : 'Salida']} />
                <Line type="monotone" dataKey="entrada" stroke="#3b82f6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="salida"  stroke="#a855f7" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-44 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
              Sin datos
            </div>
          )}
        </div>
      )}

      {/* Disk Read/Write Chart (EC2) */}
      {!isRds && m.disk_read && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Disco</h3>
            <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-green-500" /> Lectura</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-orange-500" /> Escritura</span>
            </div>
          </div>
          {diskData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={diskData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} unit=" KB" />
                <Tooltip formatter={(v: any, name: any) => [`${v} KB`, name === 'entrada' ? 'Lectura' : 'Escritura']} />
                <Line type="monotone" dataKey="entrada" stroke="#22c55e" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="salida"  stroke="#f97316" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-44 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
              Sin datos
            </div>
          )}
        </div>
      )}

      {/* RDS: CPU */}
      {isRds && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">CPU (RDS)</h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Actual: <strong>{statBadge(m.cpu?.current ?? null, '%')}</strong>
            </span>
          </div>
          {cpuData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={cpuData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="rdsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                <Tooltip formatter={(v: any) => [`${v}%`, 'CPU']} />
                <Area type="monotone" dataKey="value" stroke="#8b5cf6" fill="url(#rdsGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-44 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">Sin datos</div>
          )}
        </div>
      )}

      {/* RDS: Connections + IOPS */}
      {isRds && m.connections && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Conexiones — actual: <strong>{statBadge(m.connections?.current ?? null, 'conex')}</strong>
            </h3>
            {seriesChartData(m.connections, range.hours).length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={seriesChartData(m.connections, range.hours)} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v: any) => [`${v}`, 'Conexiones']} />
                  <Area type="monotone" dataKey="value" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.15} strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-36 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">Sin datos</div>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Storage libre — actual: <strong>{statBadge(m.free_storage?.current ?? null, 'GB')}</strong>
            </h3>
            {seriesChartData(m.free_storage, range.hours).length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={seriesChartData(m.free_storage, range.hours)} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} unit=" GB" />
                  <Tooltip formatter={(v: any) => [`${v} GB`, 'Storage libre']} />
                  <Area type="monotone" dataKey="value" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-36 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">Sin datos</div>
            )}
          </div>
        </div>
      )}

      {/* Instance ID footer */}
      {metrics?.instance_id && (
        <p className="text-xs text-gray-400 dark:text-gray-600 text-right">
          Instancia: <code className="font-mono">{metrics.instance_id}</code>
        </p>
      )}
    </div>
  );
};

// ─── InfoTab ──────────────────────────────────────────────────────────────────

const InfoTab: React.FC<{ service: Service; tenantId: string }> = ({ service, tenantId }) => {
  const navigate = useNavigate();
  const rows: { label: string; value: string | undefined }[] = [
    { label: 'Tenant ID',      value: service.tenantId || tenantId },
    { label: 'Tipo interno',   value: service.type },
    { label: 'Región',         value: service.region },
    { label: 'Instance ID (EC2)', value: service.ec2InstanceId || undefined },
    { label: 'DB Identifier',  value: service.dbInstanceId || undefined },
    { label: 'Tipo instancia', value: service.instanceType || undefined },
    { label: 'Stack Name',     value: service.stackName || undefined },
    { label: 'Costo / hora',   value: service.cost ? `$${service.cost} USD` : undefined },
    { label: 'Endpoint',       value: service.endpointUrl || undefined },
    { label: 'Creado',         value: service.createdAt ? new Date(service.createdAt).toLocaleString('es-MX') : undefined },
    { label: 'Actualizado',    value: service.updatedAt ? new Date(service.updatedAt).toLocaleString('es-MX') : undefined },
  ].filter((r) => r.value);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Información del servicio</h2>
        <dl className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map(({ label, value }) => (
            <div key={label} className="py-2.5 flex justify-between gap-4">
              <dt className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</dt>
              <dd className="text-sm text-gray-900 dark:text-white text-right font-mono break-all">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Acciones</h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/admin/clients/${service.tenantId || tenantId}`)}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
            >
              Ver cliente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── AdminServiceDetailPage ───────────────────────────────────────────────────

const AdminServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [service, setService] = useState<Service | null>(null);
  const [awsCost, setAwsCost] = useState<ServiceDetailResponse['awsCost']>(null);
  const [plan, setPlan] = useState<ServiceDetailResponse['plan']>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'metrics'>('info');

  useEffect(() => {
    if (!id) return;
    adminService.getServiceDetail(id).then((resp) => {
      if (resp.success) {
        setService(resp.service);
        setAwsCost(resp.awsCost ?? null);
        setPlan(resp.plan ?? null);
      } else {
        setLoadError(resp.error || 'Error al cargar el servicio');
      }
      setIsLoading(false);
    });
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando servicio…</p>
        </div>
      </div>
    );
  }

  if (loadError || !service) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">{loadError || 'Servicio no encontrado'}</p>
          <button
            onClick={() => navigate('/admin/services')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Volver a Servicios
          </button>
        </div>
      </div>
    );
  }

  const statusStyle = StatusColors[service.status] ?? StatusColors.STOPPED;
  const typeLabel = TypeLabels[service.type] || service.type || '—';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Back */}
          <button
            onClick={() => navigate('/admin/services')}
            className="mb-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 text-sm"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver a Servicios
          </button>

          {/* Header */}
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{service.name || id}</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {typeLabel}
                {service.tenantId && (
                  <span> • Tenant <code className="font-mono">{service.tenantId}</code></span>
                )}
              </p>
            </div>
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${statusStyle.bg} ${statusStyle.text}`}>
              {service.status}
            </span>
          </div>

          {/* Economía del servicio: qué nos cuesta AWS vs qué paga el cliente */}
          {(awsCost || plan) && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {awsCost && (
                <>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                      Costo AWS al día de hoy
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      ${awsCost.month_to_date.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {awsCost.days_elapsed} de {awsCost.days_in_month} días del mes ·{' '}
                      {awsCost.source === 'cost_explorer' ? 'real (Cost Explorer)' : 'estimado'}
                      {awsCost.scope === 'tenant' && ' · nivel tenant (sin tag por servicio)'}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                      Proyección a fin de mes
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      ${awsCost.projected_month_end.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      lineal sobre el gasto del mes en curso
                    </p>
                  </div>
                  {awsCost.since_creation != null && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                        Costo AWS acumulado
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        ${awsCost.since_creation.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {awsCost.since_creation_truncated ? 'últimos ~13 meses' : 'desde la creación'}
                        {awsCost.since_creation_start && ` (${awsCost.since_creation_start})`}
                        {awsCost.scope === 'tenant' && ' · nivel tenant'}
                      </p>
                    </div>
                  )}
                </>
              )}
              {plan && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                    Plan del cliente
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {plan.priceAmount !== undefined
                      ? `$${(plan.priceAmount / 100).toLocaleString('es-MX', { minimumFractionDigits: 2 })} ${plan.currency || 'MXN'}/mes`
                      : '—'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {plan.planName || plan.planCode}
                    {plan.variantName ? ` · ${plan.variantName}` : ''}
                    {plan.addonDetails && plan.addonDetails.length > 0 &&
                      ` · add-ons: ${plan.addonDetails.map((a) => a.addonName).join(', ')}`}
                  </p>
                  {plan.prices?.USD !== undefined && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      equivalente configurado: ${(plan.prices.USD / 100).toFixed(2)} USD/mes
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard label="Tipo" value={typeLabel} />
            <StatCard label="Región" value={service.region || 'us-east-1'} />
            <StatCard label="Costo / hora" value={service.cost ? `$${service.cost} USD` : '—'} />
            <StatCard label="Creado" value={service.createdAt ? new Date(service.createdAt).toLocaleDateString('es-MX') : '—'} />
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <div className="flex gap-6">
              {(['info', 'metrics'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {tab === 'info' ? 'Información' : 'Métricas'}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          {activeTab === 'info' && (
            <InfoTab service={service} tenantId={service.tenantId} />
          )}
          {activeTab === 'metrics' && (
            <MetricsPanel serviceId={id!} serviceType={service.type} />
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminServiceDetailPage;
